import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { GoogleAnalyticsService } from './google-analytics.service';

import { PaeViewComponent } from './pae-view/pae-view.component';
import { EntryStatusComponent } from './entry-status/entry-status.component';
import { ClusterTableComponent } from './cluster-table/cluster-table.component';
import { HeatmapComponent } from './heatmap/heatmap.component';
import { FileDownloadComponent } from './file-download/file-download.component';
import { NewsPopupComponent } from './news-popup/news-popup.component';
import { StructureViewerComponent } from './structure-viewer/structure-viewer.component';
import { FoldseekTableComponent } from './foldseek-table/foldseek-table.component';
import { DescriptionDirective } from './foldseek-table/directives/description.directive';
import { ResidueRangeDirective } from './foldseek-table/directives/residue-range.directive';
import { TedDomainsComponent } from './ted-domains/ted-domains.component';
import { PopupComponent } from './popup/popup.component';

import { SharedModule } from './shared.module';
import { NgMaterialModule } from './ngmaterial.module';
import { EntryRoutingModule } from './entry-routing.module';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { ENTRY_STORE_STATE_KEY, entryReducer } from './state/entry.reducer';
import { EntryEffects } from './state/entry.effect';
import { EntryPageHeaderComponent } from './entry/entry-page-header/entry-page-header.component';
import { EntryPageFooterComponent } from './entry/entry-page-footer/entry-page-footer.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { SummaryTabComponent } from './entry/summary-tab/summary-tab.component';
import { SimilarStructuresTabComponent } from './entry/similar-structures-tab/similar-structures-tab.component';
// import { HelpIconWithTooltipComponent } from './help-icon-with-tooltip/help-icon-with-tooltip.component';
import { LegendsPanelComponent } from './entry/legends-panel/legends-panel.component';
import { DomainsTabComponent } from './entry/domains-tab/domains-tab.component';
import { MacromoleculesTabComponent } from './entry/macromolecules-tab/macromolecules-tab.component';
import { InteractiveLeftTableComponent } from './entry/interactive-left-table/interactive-left-table.component';
import { LinearLegendsComponent } from './entry/linear-legends/linear-legends.component';

import { AnnotationsTabComponent } from './entry/annotations-tab/annotations-tab.component';
import { TextAnnotationsTabComponent } from './entry/text-annotations-tab/text-annotations-tab.component';
import { HighlightedSentencePipe } from './entry/text-annotations-tab/highlighted-sentence.pipe';
import { DownloadDropdownComponent } from './entry/download-dropdown/download-dropdown.component';
import { OverviewMobileComponent } from './entry/mobile/overview-mobile/overview-mobile.component';
import { MolstarGalleryComponent } from './entry/mobile/molstar-gallery/molstar-gallery.component';
import { EntryComponent } from './entry/entry.component';
import { MolstarComponent } from './entry/mobile/molstar/molstar.component';
import { ModelConfidenceComponent } from './entry/mobile/model-confidence/model-confidence.component';
import { DomainsComponent } from './entry/mobile/domains/domains.component';
import { MacromoleculesComponent } from './entry/mobile/macromolecules/macromolecules.component';
import { AnnotationsComponent } from './entry/mobile/annotations/annotations.component';
import { SimilarProteinsComponent } from './entry/mobile/similar-proteins/similar-proteins.component';
import { MbFoldseekTableComponent } from './entry/mobile/mb-foldseek-table/mb-foldseek-table.component';
import { MbAllbacteriaTabComponent } from './entry/mobile/mb-foldseek-table/mb-allbacteria-tab/mb-allbacteria-tab.component';
import { MbBfvdTabComponent } from './entry/mobile/mb-foldseek-table/mb-bfvd-tab/mb-bfvd-tab.component';
import { MbNtdxTabComponent } from './entry/mobile/mb-foldseek-table/mb-ntdx-tab/mb-ntdx-tab.component';
import { MbViroTabComponent } from './entry/mobile/mb-foldseek-table/mb-viro-tab/mb-viro-tab.component';
import { MbVadTabComponent } from './entry/mobile/mb-foldseek-table/mb-vad-tab/mb-vad-tab.component';
import { AfdbTabComponent } from './foldseek-table/afdb-tab/afdb-tab.component';
import { PdbTabComponent } from './foldseek-table/pdb-tab/pdb-tab.component';
import { AllbacteriaTabComponent } from './foldseek-table/allbacteria-tab/allbacteria-tab.component';
import { BfvdTabComponent } from './foldseek-table/bfvd-tab/bfvd-tab.component';
import { NtdxTabComponent } from './foldseek-table/ntdx-tab/ntdx-tab.component';
import { AddAnnotationsComponent } from './heatmap/add-annotations/add-annotations.component';
import { ZoomAnnotationsComponent } from './heatmap/zoom-annotations/zoom-annotations.component';
import { ProtvistaWrapperComponent } from './new-protvista/pv-angular-wrapper/pv-angular-wrapper.component';
import { EditAnnotationsComponent } from './heatmap/edit-annotations/edit-annotations.component';
import { ViroTabComponent } from './foldseek-table/viro-tab/viro-tab.component';
import { VadTabComponent } from './foldseek-table/vad-tab/vad-tab.component';
import { SmartSeqViewerComponent } from './shared/smart-seq-viewer/smart-seq-viewer.component';

@NgModule({
  declarations: [
    EntryComponent,
    PaeViewComponent,
    ClusterTableComponent,
    HeatmapComponent,
    NewsPopupComponent,
    StructureViewerComponent,
    FoldseekTableComponent,
    AfdbTabComponent,
    PdbTabComponent,
    AllbacteriaTabComponent,
    BfvdTabComponent,
    NtdxTabComponent,
    ViroTabComponent,
    VadTabComponent,
    DescriptionDirective,
    ResidueRangeDirective,
    TedDomainsComponent,
    PopupComponent,
    EntryPageHeaderComponent,
    EntryPageFooterComponent,
    SummaryTabComponent,
    LegendsPanelComponent,
    SimilarStructuresTabComponent,
    MacromoleculesTabComponent,
    DomainsTabComponent,
    InteractiveLeftTableComponent,
    LinearLegendsComponent,
    AnnotationsTabComponent,
    TextAnnotationsTabComponent,
    HighlightedSentencePipe,
    DownloadDropdownComponent,
    OverviewMobileComponent,
    MolstarGalleryComponent,
    MolstarComponent,
    ModelConfidenceComponent,
    DomainsComponent,
    MacromoleculesComponent,
    AnnotationsComponent,
    SimilarProteinsComponent,
    MbFoldseekTableComponent,
    MbAllbacteriaTabComponent,
    MbBfvdTabComponent,
    MbNtdxTabComponent,
    MbViroTabComponent,
    MbVadTabComponent,
    AddAnnotationsComponent,
    ZoomAnnotationsComponent,
    ProtvistaWrapperComponent,
    EditAnnotationsComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    NgOptimizedImage,
    FormsModule,
    SharedModule,
    NgMaterialModule,
    EntryRoutingModule,
    NgxSkeletonLoaderModule,
    StoreModule.forFeature(ENTRY_STORE_STATE_KEY, entryReducer),
    EffectsModule.forFeature([EntryEffects]),
    SmartSeqViewerComponent,
  ],
  providers: [GoogleAnalyticsService],
})
export class EntryModule {}
